/**
 * DetailPanel — 6-tab slide-over panel for table detail view.
 *
 * Story 1.4 AC#4, AC#5.
 *
 * Opens from right, 480px (400px lg, 320px at scale).
 * Tabs: Overview | Schema | DQ | Keys | Relations | GE.
 * Close via X, Escape, or overlay click.
 * Focus trap managed via onKeyDown + autoFocus.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import type { SampleCap } from "../../api/profiler-api";
import { OverviewTab } from "./OverviewTab";
import { SchemaTab } from "./SchemaTab";
import { DQTab } from "./DQTab";
import { KeysTab } from "./KeysTab";
import { RelationsTab } from "./RelationsTab";
import { GETab } from "./GETab";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DetailTab =
  | "overview"
  | "schema"
  | "dq"
  | "keys"
  | "relations"
  | "ge";

export interface KeyCandidate {
  columns: string[];
  /** 0-1 confidence score from the PK detector. */
  confidence: number;
  /** Optional evidence strings from the detector (e.g. "uniqueness=1.0"). */
  evidence?: string[];
  /** 'declared' when the DB constraint exists, 'inferred' otherwise. */
  type?: string;
}

export interface FKCandidate {
  columns: string[];
  target_table: string;
  target_columns?: string[];
  confidence: number;
  evidence?: string[];
}

export interface TableRelation {
  source_column: string;
  target_table: string;
  target_column: string;
  cardinality?: string;
  confidence: number;
}

export interface GEExpectation {
  expectation_type: string;
  column?: string;
  details?: string;
}

export interface TableDetail {
  tableName: string;
  score: number;
  severity: string;
  columnCount: number;
  rowCount: number;
  /** Rows actually profiled (post-sampling). When < rowCount, callers
   *  surface a "Profiled N of M (X%)" hint. Absent for full-scan profiles. */
  sampleRowCount?: number;
  /** Set when the per-table size budget cut the sample short (not just the
   *  row limit), so the panel can say the profile rests on fewer rows than
   *  were requested. */
  sampleCap?: SampleCap;
  columns?: ColumnDetail[];
  issues?: IssueDetail[];
  qualityDimensions?: Record<string, number>;
  pkCandidates?: KeyCandidate[];
  fkCandidates?: FKCandidate[];
  relations?: TableRelation[];
  geExpectations?: GEExpectation[];
}

export interface ColumnDetail {
  name: string;
  dtype: string;
  nullRate: number;
  isPII?: boolean;
  isPK?: boolean;
  isFK?: boolean;
}

export interface IssueDetail {
  column: string;
  message: string;
  severity: "excellent" | "good" | "warning" | "critical";
  dimension?: string;
  /** Optional click-to-expand sample of violating rows (up to 25) */
  sampleFailures?: Array<Record<string, unknown>>;
  /** Column order for rendering sampleFailures as a table */
  sampleFailureColumns?: string[];
}

export interface DetailPanelProps {
  table: TableDetail | null;
  activeTab?: DetailTab;
  onTabChange?: (tab: DetailTab) => void;
  onClose: () => void;
  /** Width class override for scale behavior */
  widthClass?: string;
}

// ---------------------------------------------------------------------------
// Tab config
// ---------------------------------------------------------------------------

const TABS: { id: DetailTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "schema", label: "Schema" },
  { id: "dq", label: "DQ" },
  { id: "keys", label: "Keys" },
  { id: "relations", label: "Relations" },
  { id: "ge", label: "GE" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DetailPanel({
  table,
  activeTab: controlledTab,
  onTabChange,
  onClose,
  widthClass = "w-[480px] lg:w-[400px]",
}: DetailPanelProps) {
  const [internalTab, setInternalTab] = useState<DetailTab>("overview");
  const activeTab = controlledTab ?? internalTab;
  const panelRef = useRef<HTMLDivElement>(null);

  const handleTabChange = (tab: DetailTab) => {
    setInternalTab(tab);
    onTabChange?.(tab);
  };

  // Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Focus panel on open
  useEffect(() => {
    panelRef.current?.focus();
  }, [table]);

  if (!table) return null;

  // Tab keyboard navigation (arrow keys)
  const handleTabKeyDown = (e: React.KeyboardEvent, idx: number) => {
    let nextIdx = idx;
    if (e.key === "ArrowRight") nextIdx = (idx + 1) % TABS.length;
    else if (e.key === "ArrowLeft")
      nextIdx = (idx - 1 + TABS.length) % TABS.length;
    else return;
    e.preventDefault();
    handleTabChange(TABS[nextIdx].id);
  };

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className={`
        ${widthClass} h-full flex flex-col
        border-l border-[var(--border)] glass-surface-heavy
        animate-[slideIn_200ms_ease-out]
        outline-none
      `}
      role="dialog"
      aria-label={`Detail panel: ${table.tableName}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
        <h2 className="text-heading-3 font-semibold text-[var(--foreground)] truncate">
          {table.tableName}
        </h2>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-white/10 text-[var(--muted-foreground)] transition-colors"
          aria-label="Close detail panel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div
        className="border-b border-[var(--border)]"
        role="tablist"
        aria-label="Table detail tabs"
      >
        <div className="flex px-4 gap-1">
          {TABS.map((tab, idx) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => handleTabChange(tab.id)}
              onKeyDown={(e) => handleTabKeyDown(e, idx)}
              className={`
                px-3 py-2 text-caption font-medium border-b-2 transition-colors
                ${
                  activeTab === tab.id
                    ? "border-[var(--primary)] text-[var(--foreground)]"
                    : "border-transparent text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div
        className="flex-1 overflow-y-auto p-4"
        role="tabpanel"
        aria-label={`${activeTab} tab`}
      >
        {activeTab === "overview" && <OverviewTab table={table} />}
        {activeTab === "schema" && <SchemaTab columns={table.columns ?? []} />}
        {activeTab === "dq" && <DQTab issues={table.issues ?? []} />}
        {activeTab === "keys" && (
          <KeysTab
            pkCandidates={table.pkCandidates}
            fkCandidates={table.fkCandidates}
          />
        )}
        {activeTab === "relations" && (
          <RelationsTab relations={table.relations} />
        )}
        {activeTab === "ge" && (
          <GETab
            expectations={table.geExpectations}
            tableName={table.tableName}
          />
        )}
      </div>
    </div>
  );
}
